import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import MaterialFormModal from '@/pages/master/materials/MaterialFormModal';
import ProductFormModal from '@/pages/master/products/ProductFormModal';
import CustomerFormModal from '@/pages/master/customers/CustomerFormModal';
import VendorFormModal from '@/pages/outsourcing/vendors/VendorFormModal';
import WarehouseFormModal from '@/pages/warehouse/master-data/WarehouseFormModal';
import MachineFormModal from '@/pages/system/machines/MachineFormModal';
import ProductTypeFormModal from '@/pages/master/product-types/ProductTypeFormModal';
import MaterialTypeFormModal from '@/pages/master/material-types/MaterialTypeFormModal';
import { ProcessFormModal } from '@/pages/system/processes/ProcessFormModal';
import { ToolingFormModal } from '@/pages/planning/production-orders/components/ToolingFormModal';

/**
 * Thêm mới bản ghi cha ngay từ ô chọn FK (chuẩn 03/11/2026): chỉ hiện khi người dùng có quyền tạo của danh mục đó,
 * mở đúng form của danh mục cha; tạo xong ô chọn tự chọn bản ghi mới (ComboboxFK lo phần chọn).
 * Dùng chung cho SharedCombobox và mọi ComboboxFK trong form đang tự cấp options: `<ComboboxFK {...quickAdd} … />`.
 * Danh mục chứng từ (đơn hàng, lệnh SX, PO…) không tạo từ form con — trả canAdd=false.
 */
export type QuickAddEntity =
    | 'product' | 'material' | 'productType' | 'materialType' | 'vendor' | 'customer'
    | 'warehouse' | 'machine' | 'process' | 'dieCut' | 'plate';

const LABEL: Record<QuickAddEntity, { button: string; title: string; breadcrumb: string }> = {
    product: { button: 'Thêm sản phẩm mới', title: 'Thêm mới sản phẩm', breadcrumb: 'Sản phẩm' },
    material: { button: 'Thêm vật tư mới', title: 'Thêm mới vật tư', breadcrumb: 'Vật tư' },
    productType: { button: 'Thêm loại sản phẩm', title: 'Tạo nhanh Loại sản phẩm', breadcrumb: 'Loại sản phẩm' },
    materialType: { button: 'Thêm loại vật tư', title: 'Tạo nhanh Loại vật tư', breadcrumb: 'Loại vật tư' },
    vendor: { button: 'Thêm nhà cung cấp mới', title: 'Thêm mới nhà cung cấp', breadcrumb: 'Nhà cung cấp' },
    customer: { button: 'Thêm khách hàng mới', title: 'Thêm mới khách hàng', breadcrumb: 'Khách hàng' },
    warehouse: { button: 'Thêm kho hàng mới', title: 'Thêm mới kho hàng', breadcrumb: 'Kho' },
    machine: { button: 'Thêm thiết bị mới', title: 'Thêm mới thiết bị', breadcrumb: 'Thiết bị' },
    process: { button: 'Thêm công đoạn mới', title: 'Thêm công đoạn mới', breadcrumb: 'Công đoạn' },
    dieCut: { button: 'Thêm khuôn bế mới', title: 'Thêm khuôn bế mới', breadcrumb: 'Khuôn bế' },
    plate: { button: 'Thêm bản kẽm mới', title: 'Thêm bản kẽm mới', breadcrumb: 'Bản kẽm' },
};

// Cache danh sách chọn (useFKLookup) cần làm mới sau khi tạo để bản ghi mới có trong options của mọi ô trên trang
const LOOKUP_KEY: Record<QuickAddEntity, string> = {
    product: 'products-lookup', material: 'materials-lookup', productType: 'product-types-lookup',
    materialType: 'material-types-lookup', vendor: 'vendors-lookup', customer: 'customers-lookup',
    warehouse: 'warehouses-lookup', machine: 'machines-lookup', process: 'processes-lookup',
    dieCut: 'diecuts-lookup', plate: 'plates-lookup',
};

const MODAL_SIZE: Partial<Record<QuickAddEntity, 'sm' | 'md' | 'lg' | 'xl' | 'full'>> = {
    product: 'full', vendor: 'full', customer: 'full', material: 'full', machine: 'full', warehouse: 'xl', process: 'lg',
};

export function isQuickAddEntity(type: string): type is QuickAddEntity {
    return type in LABEL;
}

/** Quyền hiện nút thêm mới của ô chọn FK (thuần, để kiểm thử) */
export function canAddEntity(type: string, hasPermission: (module: string, action: string) => boolean): boolean {
    const entity = isQuickAddEntity(type) ? type : null;
    switch (entity) {
        case 'product': return hasPermission('products', 'create');
        case 'material': return hasPermission('materials', 'create');
        case 'productType': return hasPermission('products', 'create') || hasPermission('configs', 'create');
        case 'materialType': return hasPermission('materials', 'create') || hasPermission('configs', 'create');
        case 'vendor': return hasPermission('vendors', 'create');
        case 'customer': return hasPermission('customers', 'create');
        // Trước 03/11/2026 năm loại dưới luôn true (ai cũng thấy nút thêm) — nay theo đúng quyền tạo
        case 'warehouse': return hasPermission('warehouses', 'create') || hasPermission('inventory', 'create');
        case 'machine': return hasPermission('machines', 'create') || hasPermission('production', 'create');
        case 'process': return hasPermission('processes', 'create') || hasPermission('configs', 'create');
        case 'dieCut': return hasPermission('molds', 'create') || hasPermission('technical', 'create');
        case 'plate': return hasPermission('plates', 'create') || hasPermission('technical', 'create');
        default: return false;
    }
}

export interface EntityQuickAddOptions {
    params?: Record<string, any>;
    breadcrumb?: string[];
    /** Ghi đè quyền (VD form chỉ cho chọn) */
    canAdd?: boolean;
    quickAddLabel?: string;
    addFormTitle?: string;
    addFormContent?: React.ReactNode;
    onQuickAddClick?: (name: string) => void;
    onCreated?: (id: string) => void;
}

const childBreadcrumb = (parent: string[] | undefined, child: string) =>
    !parent?.length ? [child] : parent[parent.length - 1] === child ? parent : [...parent, child];

export function useEntityQuickAdd(type: string, opts: EntityQuickAddOptions = {}) {
    const { hasPermission } = useRoleAccess();
    const queryClient = useQueryClient();
    const entity = isQuickAddEntity(type) ? type : null;
    const userOnCreated = opts.onCreated;
    const onCreated = useCallback(
        (id: string) => {
            if (entity) {
                queryClient.invalidateQueries({ queryKey: [LOOKUP_KEY[entity]] });
                queryClient.invalidateQueries({ queryKey: [['combobox', 'getOptions']] });
            }
            userOnCreated?.(id);
        },
        [entity, queryClient, userOnCreated],
    );

    const canAdd = useMemo(
        () => (opts.canAdd !== undefined ? opts.canAdd : canAddEntity(type, hasPermission)),
        [opts.canAdd, type, hasPermission],
    );

    const p = opts.params;
    let addFormContent = opts.addFormContent;
    if (canAdd && entity && !addFormContent && !opts.onQuickAddClick) {
        const bc = childBreadcrumb(opts.breadcrumb, LABEL[entity].breadcrumb);
        switch (entity) {
            case 'product':
                addFormContent = <ProductFormModal initialData={{ customerId: p?.customerId }} lockedFields={p?.customerId ? ['customerId'] : []} params={p} breadcrumb={bc} />;
                break;
            case 'material':
                addFormContent = <MaterialFormModal initialData={{ vendorId: p?.vendorId }} lockedFields={p?.vendorId ? ['vendorId'] : []} params={p} breadcrumb={bc} />;
                break;
            case 'customer':
                addFormContent = <CustomerFormModal params={p} initialData={p} breadcrumb={bc} />;
                break;
            case 'vendor':
                addFormContent = <VendorFormModal params={p} initialData={p} />;
                break;
            case 'warehouse':
                addFormContent = <WarehouseFormModal />;
                break;
            case 'machine':
                addFormContent = <MachineFormModal />;
                break;
            case 'productType':
                addFormContent = <ProductTypeFormModal params={p} initialData={p} breadcrumb={bc} />;
                break;
            case 'materialType':
                addFormContent = <MaterialTypeFormModal params={p} initialData={p} breadcrumb={bc} />;
                break;
            case 'process':
                addFormContent = <ProcessFormModal params={p} initialData={p} />;
                break;
            case 'dieCut':
                addFormContent = <ToolingFormModal initialType="DIE_CUT" params={p} initialData={p} />;
                break;
            case 'plate':
                addFormContent = <ToolingFormModal initialType="PLATE" params={p} initialData={p} />;
                break;
        }
    }

    return {
        canAdd,
        quickAddLabel: opts.quickAddLabel || (entity ? LABEL[entity].button : undefined),
        addFormTitle: opts.addFormTitle || (entity ? LABEL[entity].title : 'Thêm mới'),
        addFormContent,
        addModalSize: (entity && MODAL_SIZE[entity]) || 'md',
        onCreated,
        ...(opts.onQuickAddClick ? { onQuickAddClick: opts.onQuickAddClick } : {}),
    } as const;
}
